import { useEffect, useState } from 'react';
import { getSearchParams, handleSearchParams } from '../../../../helpers/handleSearchParams';
import {
  getReportsReplenishment,
  getReportsReplenishmentMonthly,
} from '../../../../services/reports/replenishmentService';
import { PAGE_LIMIT, toApiDate } from './constants';

/**
 * Fetcher principal del informe de Reposición. Lanza el JSON
 * (`/replenishment`) en cada cambio de filtros y, en paralelo, la
 * agregación mensual (`/replenishment/monthly`) que alimenta el line
 * chart del capítulo "Evolución mensual".
 */
export const useReplenishmentData = (filterState) => {
  const [data, setData] = useState(null);
  const [monthly, setMonthly] = useState(null);

  const [sortBy, setSortBy] = useState(() => getSearchParams('sortBy') || 'family');
  const [sortDir, setSortDir] = useState(() => (getSearchParams('sortDir') === 'desc' ? 'desc' : 'asc'));

  const handleSort = (columnId) => {
    if (columnId === sortBy) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(columnId);
    setSortDir('desc');
  };

  const {
    range,
    depositsValue,
    depositRolesValue,
    depositGroupsValue,
    familyQuery,
    filters,
    ready,
    validFilters,
    persistInUrl,
  } = filterState;

  useEffect(() => {
    if (!ready) return;
    persistInUrl();
    handleSearchParams('sortBy', sortBy);
    handleSearchParams('sortDir', sortDir);

    const dateFrom = toApiDate(range.from);
    const dateToExclusive = toApiDate(range.toExclusive);
    if (!dateFrom || !dateToExclusive) return;

    const args = {
      dateFrom,
      dateToExclusive,
      page: 1,
      limit: PAGE_LIMIT,
      sortBy,
      sortDir,
      ...validFilters,
    };
    if (depositsValue) args.deposits = depositsValue;
    if (depositRolesValue) args.depositRoles = depositRolesValue;
    if (depositGroupsValue) args.depositGroups = depositGroupsValue;
    if (familyQuery) args.family = familyQuery;

    getReportsReplenishment(args)
      .then((r) => setData(r.data))
      .catch(() => setData(null));

    // El line chart consume los últimos 12 meses — los filtros se
    // respetan pero el rango temporal es independiente del `range`.
    const monthlyArgs = { ...args, monthsBack: 12 };
    getReportsReplenishmentMonthly(monthlyArgs)
      .then((r) => setMonthly(r.data))
      .catch(() => setMonthly(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    range.from,
    range.toExclusive,
    depositsValue,
    depositRolesValue,
    depositGroupsValue,
    familyQuery,
    filters,
    ready,
    sortBy,
    sortDir,
    validFilters,
  ]);

  return { data, monthly, sortBy, sortDir, handleSort };
};
